import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";

// Each collection maps to one or more catalog categories + its own SEO copy.
const COLLECTIONS = {
  soccer: {
    label: "Soccer",
    categories: ["countries", "crest", "champions"],
    h1: "Custom Soccer Jerseys on Hoodies & Tees",
    tagline: "National Teams · Crests · World Champions",
    intro:
      "Rep your country in style. Our original fan-made soccer designs — national team crests and World Champions artwork — are printed on premium hoodies and tees in 7 colors, with your name and number on the back. The perfect custom kit for the 2026 World Cup and every match day.",
    title: "Custom Soccer Jerseys — National Team Hoodies & Tees | Jersey Blend",
    description:
      "Custom soccer jerseys on premium hoodies and tees. Original national team crests & World Champions designs — add your name and number, 7 colors. Perfect for the 2026 World Cup.",
    keywords:
      "custom soccer jersey, 2026 World Cup hoodie, national team crest hoodie, personalized football shirt, soccer fan hoodie with name and number, country soccer jersey",
  },
  nfl: {
    label: "NFL",
    categories: ["nfl"],
    h1: "Custom NFL Football Hoodies & Tees",
    tagline: "Original Team Designs · Your Name & Number",
    intro:
      "Gridiron-ready fan gear. Our original football-team designs are printed on premium hoodies and tees in 7 colors, personalized with your name and number on the back. Game-day style for every fan.",
    title: "Custom NFL Football Hoodies & Tees — Name & Number | Jersey Blend",
    description:
      "Custom NFL football hoodies and tees with original team designs. Add your name and number on the back — 7 garment colors. Game-day fan gear that ships in 3–7 days.",
    keywords:
      "custom NFL hoodie, football hoodie, custom football jersey, game day hoodie, personalized football shirt with name and number, NFL fan gear",
  },
  nba: {
    label: "NBA",
    categories: ["nba"],
    h1: "Custom NBA Basketball Hoodies & Tees",
    tagline: "Original Team Designs · Your Name & Number",
    intro:
      "Court-side style. Our original basketball-team designs are printed on premium hoodies and tees in 7 colors, personalized with your name and number on the back. Rep your team all season long.",
    title: "Custom NBA Basketball Hoodies & Tees — Name & Number | Jersey Blend",
    description:
      "Custom NBA basketball hoodies and tees with original team designs. Add your name and number — 7 garment colors. Personalized fan gear that ships in 3–7 days.",
    keywords:
      "custom NBA hoodie, custom basketball jersey, basketball hoodie, personalized basketball shirt with name and number, NBA fan gear",
  },
};

export default function CollectionPage() {
  const { slug } = useParams();
  const cfg = COLLECTIONS[slug];
  const [products, setProducts] = useState(null);

  useEffect(() => {
    if (!cfg) return;
    setProducts(null);
    api
      .get("/products")
      .then((r) => {
        const set = new Set(cfg.categories);
        setProducts((r.data || []).filter((p) => set.has(p.category)));
      })
      .catch(() => setProducts([]));
  }, [slug]);

  if (!cfg) return <Navigate to="/" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: cfg.title,
    description: cfg.description,
    url: `https://jerseyblend.net/collections/${slug}`,
  };

  return (
    <div className="bg-background min-h-screen" data-testid={`collection-${slug}`}>
      <Seo
        title={cfg.title}
        description={cfg.description}
        keywords={cfg.keywords}
        path={`/collections/${slug}`}
        jsonLd={jsonLd}
      />
      {/* Hero copy */}
      <div className="px-6 md:px-12 lg:px-16 pt-16 pb-10 border-b border-white/10">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-primary mb-4">{cfg.tagline}</p>
        <h1 className="font-anton text-4xl sm:text-5xl lg:text-6xl uppercase leading-none text-white max-w-4xl">
          {cfg.h1}
        </h1>
        <p className="text-white/60 mt-6 leading-relaxed max-w-2xl text-base">{cfg.intro}</p>
        <div className="flex flex-wrap gap-3 mt-8 text-xs uppercase tracking-[0.15em] font-bold">
          {Object.keys(COLLECTIONS).map((s) => (
            <Link
              key={s}
              to={`/collections/${s}`}
              data-testid={`collection-link-${s}`}
              className={`px-5 py-2.5 border transition-colors ${
                s === slug
                  ? "bg-primary text-black border-primary"
                  : "bg-transparent text-white border-white/20 hover:border-primary hover:text-primary"
              }`}
            >
              {COLLECTIONS[s].label}
            </Link>
          ))}
          <Link
            to="/collections/champions"
            className="px-5 py-2.5 border bg-transparent text-white/70 border-white/20 hover:border-primary hover:text-primary transition-colors"
          >
            World Champions
          </Link>
          <Link
            to="/#shop"
            className="px-5 py-2.5 border bg-transparent text-white/70 border-white/20 hover:border-primary hover:text-primary transition-colors"
          >
            All Products
          </Link>
        </div>
      </div>

      {/* Grid */}
      <div className="px-6 md:px-12 lg:px-16 py-12">
        {products === null ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : products.length === 0 ? (
          <p className="text-white/50 py-24 text-center uppercase tracking-widest font-bold">No products found.</p>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/40 mb-6">
              {products.length} {cfg.label} drops
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 md:gap-8">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
